import { useEffect, useState } from 'react';
import Select from '../../../juristec-ui/core/SelectNew';
import Tooltip from '../../../juristec-ui/core/Tooltip';
import IconButton from '../../../juristec-ui/core/IconButton';
import Button from '../../../juristec-ui/core/Button';
import ActionsGroup from '../../../juristec-ui/core/ActionsGroup';
import FilePreview from '../FilePreview';
import KpiForm from './extras/KpiForm/KpiForm';
import AssistantSuggestion from './extras/AssistantSuggestion/AssistantSuggestion';
import { type GptTokens } from '../../AssistentTokensBadge/AssistentTokensBadge';

import { SelectOption } from '../../../types';
import { kpiType } from './types';

import {
  KpisContainer,
  MainContainer,
  ToolbarContainer,
  HeaderContainer,
  KpiCard,
  KpiCardIcon,
  KpiCardContent,
  KpiCardHeader,
  CardAssistantBadge,
  KpiContainerEmpty,
  InputWrapper,
} from './styled/QuickDash.styled';

import {
  AssistantDash,
  Edit,
  Eye2,
  Sparkles,
  Trash,
} from '../../../juristec-ui/icons';

import ChartIcon from './ChartIcon';

const KPIS_LIMIT = 20;

const filterValueLabels: Record<string, string> = {
  most: '{N} Maiores (sem empate)',
  most_inc: '{N} Maiores (com empate)',
  least: '{N} Menores (sem empate)',
  least_inc: '{N} Menores (com empate)',
};

const getFilterValueLabel = (key: string, value: number): string => (
  (filterValueLabels[key] ?? '').replace('{N}', value.toString())
);

interface Dataframe {
  columns: Record<string, string>[];
  data: Record<string, string>,
  types: Record<string, string>;
}

interface QuickDashProps {
  allowGpt?: boolean;
  filesOpts?: SelectOption[];
  hide: () => void;
  closeModal: (nodeTarget?: string) => void;
  submit: (fileId: string, kpisData: kpiType[]) => void;
  setModalConfig: (config: Record<string, any>) => void;
  getFileSample: (fileId: string) => Promise<Dataframe | null>;
  warningSelectFile: (callback: () => void) => void;
  virtualAssistant: (fileId: string, prompt: string) => Promise<kpiType[] | null>;
  gptTokens: GptTokens;
  blockGeneration: boolean;
}

const defaultProps = {
  allowGpt: false,
  filesOpts: [],
};

const QuickDash = ({
  allowGpt = defaultProps.allowGpt,
  filesOpts = defaultProps.filesOpts,
  gptTokens,
  setModalConfig,
  warningSelectFile,
  getFileSample,
  hide,
  closeModal,
  submit,
  virtualAssistant,
  blockGeneration,
}: QuickDashProps) => {
  const [showVSModal, setShowVSModal] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<SelectOption | null>(null);
  const [fileColumns, setFileColumns] = useState<Map<string, SelectOption>>(new Map());
  const [kpis, setKpis] = useState<Map<string, kpiType>>(new Map());
  const [dataFrame, setDataFrame] = useState<Dataframe | null>(null);

  const handleSelectedFile = async (selected: SelectOption) => {
    setSelectedFile(selected);
    if (selected?.value !== '') {
      setKpis(new Map());
      const df = await getFileSample(selected.value);
      if (df) {
        setDataFrame(df);
        const opts: [string, SelectOption][] = Object.entries(df.types).map(([k, v]) => ([k, {
          label: k, value: k, id: k, type: v,
        } as SelectOption]));
        setFileColumns(new Map(opts));
      }
    }
  };

  const handleWarningSelectFile = (selected: SelectOption) => {
    if (kpis.size > 0) {
      warningSelectFile(() => handleSelectedFile(selected));
    } else handleSelectedFile(selected);
  };

  const handleKpi = (kpi: kpiType) => {
    setKpis((prev) => new Map(prev).set(kpi.id, kpi));
  };

  const removeKpi = (kpi: kpiType) => {
    setKpis((prev) => {
      const aux = new Map(prev);
      aux.delete(kpi.id);
      return aux;
    });
  };

  const hideKpiForm = () => closeModal('modal_kpi_form');

  const openKpiForm = (kpi?: kpiType) => {
    setModalConfig({
      title: `${kpi ? 'Editar' : 'Adicionar'} Kpi`,
      nodeTarget: 'modal_kpi_form',
      disableFocus: true,
      children: (
        <KpiForm
          fileColumns={fileColumns}
          submitKpi={handleKpi}
          hide={hideKpiForm}
          kpi={kpi}
        />
      ),
    });
  };

  const handleOpenKpiForm = () => openKpiForm();

  const hideAssistantSuggestion = () => {
    closeModal('modal_assistant_sugestion');
    setShowVSModal(false);
  };

  const handleQuestion = async (question: string) => {
    const suggestions = await virtualAssistant(selectedFile!.value, question);
    if (suggestions && suggestions.length > 0) {
      setKpis((prev) => (
        suggestions.reduce((aux, sugg) => (
          aux.set(sugg.id, {
            ...sugg,
            author: 'assistant',
          })
        ), new Map(prev))
      ));
      hideAssistantSuggestion();
    }
  };

  const openAssistantSuggestion = () => {
    setModalConfig({
      title: "Sugestão de KPI's (BETA)",
      nodeTarget: 'modal_assistant_sugestion',
      disableFocus: true,
      closeFunc: hideAssistantSuggestion,
      children: (
        <AssistantSuggestion
          submitQuestion={handleQuestion}
          hide={hideAssistantSuggestion}
          gptTokens={gptTokens}
        />
      ),
    });
  };

  useEffect(() => {
    if (showVSModal) openAssistantSuggestion();
  }, [gptTokens.used, gptTokens.bonus, showVSModal]);

  const setShowVSModalTrue = () => setShowVSModal(true);

  const handleSubmit = () => {
    const kpisData = [...kpis.values()];
    if (selectedFile?.value && kpisData.length > 0) {
      submit(selectedFile.value, kpisData);
    }
  };

  const hideDataFrameModal = () => {
    closeModal('dataframe_modal');
  };

  const openDataFrameModal = () => {
    setModalConfig({
      title: selectedFile?.label,
      nodeTarget: 'dataframe_modal',
      disableFocus: true,
      closeFunc: hideDataFrameModal,
      children: !!dataFrame && (
        // @ts-expect-error not typed yet
        <FilePreview dataFrame={dataFrame} />
      ),
    });
  };

  return (
    <>
      <MainContainer style={{ width: '50vw', height: (kpis.size ?? 0) > 0 ? '65vh' : 'auto' }}>
        <span>
          Crie múltiplos KPIs em seu dashboard. Cada geração de KPIs é
          feita com base em um único arquivo.
        </span>
        <HeaderContainer>
          <InputWrapper>
            <Select
              label="Selecione o arquivo"
              // @ts-expect-error not typed yet
              value={selectedFile}
              options={filesOpts}
              onChange={handleWarningSelectFile}
              atModal
              searchable
              sortBy="ascending"
            />
            {selectedFile?.value ? (
              <Tooltip text="Visualizar Dados">
                <IconButton
                  color="primary"
                  onClick={openDataFrameModal}
                  style={{ marginLeft: 5 }}
                >
                  <Eye2 />
                </IconButton>
              </Tooltip>
            ) : null}
          </InputWrapper>
        </HeaderContainer>
        {selectedFile?.value && selectedFile?.value?.length > 0 && (
          <>
            {kpis?.size <= 0 ? (
              <KpiContainerEmpty>
                <span>{'Sem KPI\'s para serem criados. Adicione um novo esquema em:'}</span>
                <div>
                  <Button
                    size="small"
                    onClick={handleOpenKpiForm}
                  >
                    Adicionar Kpi
                  </Button>
                  {allowGpt && (
                    <>
                      <span>ou</span>
                      <Tooltip
                        textWhen={blockGeneration}
                        text="O administrador desativou o uso deste agente para sua conta"
                        className="gen-button-tooltip"
                        direction="top"
                        atModal
                      >
                        <Button
                          size="small"
                          onClick={setShowVSModalTrue}
                          disabled={blockGeneration}
                        >
                          <AssistantDash />
                          Assistente Virtual
                        </Button>
                      </Tooltip>
                    </>
                  )}
                </div>
              </KpiContainerEmpty>
            ) : (
              <>
                <ToolbarContainer limit={KPIS_LIMIT} size={kpis.size ?? 0}>
                  <span style={{ marginRight: 'auto', marginTop: 'auto' }}>
                    {kpis.size ?? 0}
                    /
                    {KPIS_LIMIT}
                    {kpis.size > KPIS_LIMIT && (
                      <span style={{ marginLeft: '5px' }}>
                        Limite máximo de KPI&apos;s ultrapassado!
                      </span>
                    )}
                  </span>
                  <Button
                    size="small"
                    onClick={handleOpenKpiForm}
                  >
                    Adicionar Kpi
                  </Button>
                  {allowGpt && (
                    <Tooltip
                      textWhen={blockGeneration}
                      text="O administrador desativou o uso deste agente para sua conta"
                      className="gen-button-tooltip"
                      direction="top"
                      atModal
                    >
                      <Button
                        size="small"
                        onClick={setShowVSModalTrue}
                        style={{ marginLeft: '5px' }}
                        disabled={blockGeneration}
                      >
                        <AssistantDash />
                        Assistente Virtual
                      </Button>
                    </Tooltip>
                  )}
                </ToolbarContainer>
                <KpisContainer>
                  {[...kpis.values()].map((kpi) => (
                    <KpiCard
                      as={Tooltip}
                      text={kpi.comment}
                      textWhen={Boolean(kpi.comment)}
                      atModal
                      direction="top"
                      key={kpi.id}
                      className={kpi.author === 'assistant' ? 'assistant-mark' : ''}
                    >
                      <KpiCardIcon>
                        <ChartIcon type={kpi.type} />
                      </KpiCardIcon>
                      <KpiCardContent>
                        <KpiCardHeader>
                          <h5>{kpi.name}</h5>
                          <div className="action-group">
                            <Tooltip text="Editar" atModal>
                              <IconButton size="small" onClick={() => openKpiForm(kpi)}>
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip text="Remover" atModal>
                              <IconButton size="small" onClick={() => removeKpi(kpi)}>
                                <Trash />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </KpiCardHeader>
                        <span>
                          <b>Valores: </b>
                          {kpi.values.map((v) => v.column).join(', ')}
                        </span>
                        {Boolean(kpi.lines?.[0]?.column) && (
                          <span>
                            <b>Linha: </b>
                            {kpi.lines[0].column}
                          </span>
                        )}
                        {Boolean(kpi.columns?.[0]?.column) && (
                          <span>
                            <b>Coluna: </b>
                            {kpi.columns[0].column}
                          </span>
                        )}
                        {Boolean(kpi.filterValues?.[0]?.ftype) && (
                          <span>
                            <b>Filtro de valores: </b>
                            {getFilterValueLabel(
                              kpi.filterValues[0].ftype,
                              kpi.filterValues?.[0]?.values?.[0] ?? 1,
                            )}
                          </span>
                        )}
                        {kpi.author === 'assistant' && (
                          <CardAssistantBadge>
                            <Tooltip
                              text="Sugerido pelo assistente virtual"
                              atModal
                              direction="top"
                            >
                              <Sparkles />
                            </Tooltip>
                          </CardAssistantBadge>
                        )}
                      </KpiCardContent>
                    </KpiCard>
                  ))}
                </KpisContainer>
              </>
            )}
          </>
        )}
      </MainContainer>
      <ActionsGroup>
        <Button
          style={{ margin: '5px' }}
          onClick={hide}
          variant="outlined"
          size="large"
        >
          Cancelar
        </Button>
        <Button
          style={{ margin: '5px' }}
          onClick={handleSubmit}
          size="large"
          disabled={!(kpis.size > 0 && kpis.size <= KPIS_LIMIT)}
        >
          Salvar
        </Button>
      </ActionsGroup>
    </>
  );
};

export default QuickDash;
